/**
 * App.js — Main application component for Zendesk AI Testing Sandbox
 *
 * Views (sidebar nav — ordered by user journey):
 *   - Home          → HomeTab (first-visit default, then remembers last tab)
 *   - Scenarios     → ScenariosTab
 *   - Zendesk       → ZendeskTickets
 *   - Agent Config  → AgentConfigTab (includes KB configuration as a sub-section)
 *   - Benchmark 🔒  → BenchmarkTab (Starter+ only)
 *
 * KB configuration was previously a separate nav item that opened a modal.
 * It is now embedded inside AgentConfigTab as a persistent inline section,
 * which is the correct conceptual home for it.
 */

import React, { useState, useEffect } from 'react';
import { useAuth, useUser, UserButton } from '@clerk/react';
import SignUpPage from './SignUpPage';
import SignInPage from './SignInPage';
import ZendeskTickets from './ZendeskTickets';
import BenchmarkTab from './BenchmarkTab';
import AgentConfigTab from './AgentConfigTab';
import ScenariosTab from './ScenariosTab';
import HomeTab from './HomeTab';
import UpgradeModal from './UpgradeModal';
import { useBilling } from './hooks/useBilling';
import { useApi } from './hooks/useApi';
import './App.css';

// First-visit logic — show Home on first load, then remember last tab
const LAST_TAB_KEY = 'last_active_tab';
const FIRST_VISIT_KEY = 'has_visited';

function getInitialView() {
  const hasVisited = localStorage.getItem(FIRST_VISIT_KEY) === 'true';
  if (!hasVisited) return 'home';
  // If user was last on 'kb' (old nav item), redirect to agent-config
  const saved = localStorage.getItem(LAST_TAB_KEY) || 'scenarios';
  return saved === 'kb' ? 'agent-config' : saved;
}

function App() {
  // ── Clerk auth — must be declared before all other hooks ──────
  const { isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();

  // ── Top-level navigation ──────────────────────────────────────
  const [mainView, setMainView] = useState(getInitialView);
  const [triggerNewConfig, setTriggerNewConfig] = useState(0);
  const [activeAgentConfig, setActiveAgentConfig] = useState(null);

  // Persist last tab and mark visited
  const navigateTo = (view) => {
    setMainView(view);
    localStorage.setItem(LAST_TAB_KEY, view);
    localStorage.setItem(FIRST_VISIT_KEY, 'true');
  };

  // ── Billing ───────────────────────────────────────────────────
  const { billing, checkout, openPortal } = useBilling();
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  // ── API ───────────────────────────────────────────────────────
  const { authFetch } = useApi();

  // ── Demo environment state ────────────────────────────────────
  // Demo mode is triggered by ?demo=true in the URL (e.g. app.testmyagent.io?demo=true).
  // This avoids Clerk satellite app complexity — everything runs on app.testmyagent.io.
  // The flag is persisted to sessionStorage so it survives Clerk's auth redirect loop
  // without staying permanently attached to the account.
  const DEMO_MODE = (() => {
    const param = new URLSearchParams(window.location.search).get('demo') === 'true';
    if (param) sessionStorage.setItem('demo_mode', 'true');
    return param || sessionStorage.getItem('demo_mode') === 'true';
  })();
  const [isDemo, setIsDemo] = useState(DEMO_MODE);
  const [betaCta, setBetaCta] = useState('mailto:fernando@cxperiences.com?subject=Beta Access Request');
  const [isDemoSeeding, setIsDemoSeeding] = useState(false);

  // ── Auto-load active agent config on mount ────────────────────
  useEffect(() => {
    if (!isSignedIn) return;

    if (DEMO_MODE) {
      // Demo subdomain — skip the guard API call, we already know this is demo.
      // Fetch beta CTA URL from backend in case it's been customised, but don't
      // block on it — the default mailto is already set in state.
      authFetch('/api/demo/guard')
        .then(res => { if (res?.beta_cta_url) setBetaCta(res.beta_cta_url); })
        .catch(() => {});

      // Seed this workspace if it hasn't been seeded yet
      authFetch('/api/demo/status')
        .then(async res => {
          if (!res?.is_seeded) {
            setIsDemoSeeding(true);
            try {
              await authFetch('/api/demo/seed', { method: 'POST' });
            } catch (e) {
              console.warn('Demo seed failed:', e);
            } finally {
              setIsDemoSeeding(false);
            }
          }
        })
        .catch(() => {});
    } else {
      // Production app — check backend guard in case DEMO_WORKSPACE_ID is set
      authFetch('/api/demo/guard')
        .then(res => {
          if (res?.is_demo) {
            setIsDemo(true);
            if (res.beta_cta_url) setBetaCta(res.beta_cta_url);
          }
        })
        .catch(() => {});

      // Seed only if backend confirms this is a demo workspace
      authFetch('/api/demo/status')
        .then(async res => {
          if (res?.is_demo_workspace && !res?.is_seeded) {
            setIsDemoSeeding(true);
            try {
              await authFetch('/api/demo/seed', { method: 'POST' });
            } catch (e) {
              console.warn('Demo seed failed:', e);
            } finally {
              setIsDemoSeeding(false);
            }
          }
        })
        .catch(() => {});
    }

    authFetch('/api/agent-configs')
      .then(res => {
        // API returns { configs: [...] }, not a bare array
        const configs = res?.configs || (Array.isArray(res) ? res : []);
        if (configs.length > 0) {
          const active = configs.find(c => c.is_active) || configs[0];
          setActiveAgentConfig(active);
        }
      })
      .catch(() => {});
    // Check if KB articles exist for the Home checklist step
    authFetch('/api/kb/status')
      .then(res => { if (res?.configured) setHasKbArticles(true); })
      .catch(() => {});
  }, [isSignedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Theme toggle ──────────────────────────────────────────────
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(prev => prev === 'dark' ? 'light' : 'dark');

  // ── Shared KB config — lifted from AgentConfigTab ─────────────
  // KB is now configured inside AgentConfigTab's "Global KB" section.
  // AgentConfigTab calls onKbChange whenever the user saves a KB selection,
  // so ZendeskTickets and BenchmarkTab still receive the shared state.
  const [sharedKbArticles, setSharedKbArticles] = useState(null);
  const [hasKbArticles, setHasKbArticles] = useState(false);
  const [sharedAgentPrompt, setSharedAgentPrompt] = useState('');

  const handleKbChange = ({ kb_articles, agent_system_prompt }) => {
    setSharedKbArticles(kb_articles && kb_articles.length > 0 ? kb_articles : null);
    setSharedAgentPrompt(agent_system_prompt || '');
  };

  // ── OAuth + checkout callback handling ────────────────────────
  const [oauthMessage, setOauthMessage] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const zendeskStatus = params.get('zendesk');
    const subdomain = params.get('subdomain');
    const checkoutStatus = params.get('checkout');

    if (zendeskStatus === 'connected' && subdomain) {
      setOauthMessage(`✓ Connected to ${subdomain}.zendesk.com`);
      navigateTo('zendesk');
      window.history.replaceState({}, document.title, window.location.pathname);
      setTimeout(() => setOauthMessage(null), 5000);
    }

    if (checkoutStatus === 'success') {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auth gate — MUST come after ALL hooks ─────────────────────
  if (!isLoaded) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', background: '#0a0a12', color: '#4e4b6a',
        fontFamily: "'IBM Plex Mono', monospace", fontSize: 13, letterSpacing: '0.06em',
      }}>
        loading...
      </div>
    );
  }

  // Demo workspace seeding — show brief loading screen so the user doesn't
  // land in an empty workspace while the seed call is in flight.
  if (isSignedIn && isDemoSeeding) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '100vh', background: '#0a0a12',
        gap: 16,
      }}>
        <div style={{
          width: 40, height: 40, borderRadius: '50%',
          border: '3px solid #2d2d4e', borderTopColor: '#7c3aed',
          animation: 'spin 0.8s linear infinite',
        }} />
        <div style={{ color: '#94a3b8', fontFamily: "'IBM Plex Mono', monospace", fontSize: 13 }}>
          Setting up your workspace...
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!isSignedIn) {
    const path = window.location.pathname;
    if (path === '/sign-up') return <SignUpPage />;
    return <SignInPage />;
  }

  // ── Page titles ───────────────────────────────────────────────
  const pageTitles = {
    home:           'Home',
    scenarios:      'Scenarios',
    zendesk:        'Zendesk Tickets',
    benchmark:      'Benchmark',
    'agent-config': 'Agent Config',
  };

  const tierLabel =
    billing?.tier === 'professional' ? 'Professional' :
    billing?.tier === 'starter'      ? 'Starter'       :
    'Free Plan';

  // ── Nav items ─────────────────────────────────────────────────
  // KB is no longer a top-level nav item. It lives inside Agent Config.
  const navItems = [
    { id: 'home',         icon: '🏠', label: 'Home' },
    { id: 'scenarios',    icon: '📋', label: 'Scenarios' },
    { id: 'zendesk',      icon: '🎫', label: 'Zendesk Tickets' },
    { id: 'agent-config', icon: '🤖', label: 'Agent Config' },
    {
      id: 'benchmark', icon: '⚖️', label: 'Benchmark',
      gated: !billing?.features?.benchmark,
    },
  ];

  return (
    <div className="App sidebar-layout">

      {/* ══════════════════════════════════════════════════════════ */}
      {/* LEFT SIDEBAR NAV                                          */}
      {/* ══════════════════════════════════════════════════════════ */}
      <aside className="sidebar">
        {/* Logo + wordmark */}
        <div className="sidebar-brand">
          <svg viewBox="0 0 40 40" width="28" height="28" style={{ flexShrink: 0 }}>
            <defs>
              <linearGradient id="logoGrad2" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style={{ stopColor: '#667eea' }} />
                <stop offset="100%" style={{ stopColor: '#764ba2' }} />
              </linearGradient>
            </defs>
            <rect width="40" height="40" rx="9" fill="url(#logoGrad2)" />
            <path d="M20 9 L10 14.5 L20 20 L30 14.5 Z" fill="none" stroke="#fff" strokeWidth="2" strokeLinejoin="round" />
            <path d="M10 20 L20 25.5 L30 20" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M10 25.5 L20 31 L30 25.5" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <div className="sidebar-brand-text">
            <span className="sidebar-brand-name">Zendesk <span className="sidebar-brand-highlight">AI</span></span>
            <span className="sidebar-brand-sub">Testing Sandbox</span>
          </div>
        </div>

        {/* Nav items */}
        <nav className="sidebar-nav">
          {navItems.map(item => (
            <button
              key={item.id}
              className={`sidebar-nav-item ${mainView === item.id ? 'active' : ''}`}
              onClick={() => {
                if (item.gated) {
                  setUpgradeModalOpen(true);
                  return;
                }
                navigateTo(item.id);
              }}
            >
              <span className="sidebar-nav-icon">{item.icon}</span>
              <span className="sidebar-nav-label">{item.label}</span>
              {item.gated && <span className="sidebar-nav-lock">🔒</span>}
            </button>
          ))}
        </nav>

        {/* Bottom: user info + theme toggle */}
        <div className="sidebar-bottom">
          <div className="sidebar-user">
            <UserButton
              appearance={{
                variables: {
                  colorBackground: '#0f172a',
                  colorInputBackground: '#1a1a1a',
                  colorPrimary: '#7c3aed',
                },
                elements: {
                  userButtonPopoverCard: {
                    backgroundColor: '#0f172a',
                    border: '1px solid #1e293b',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                  },
                  userButtonPopoverActionButton: { color: '#ffffff' },
                  userButtonPopoverActionButtonText: { color: '#ffffff' },
                  userPreviewMainIdentifier: { color: '#ffffff' },
                  userPreviewSecondaryIdentifier: { color: '#9ca3af' },
                },
              }}
            />
            <div className="sidebar-user-details">
              <span className="sidebar-user-name">
                {user?.firstName || user?.emailAddresses?.[0]?.emailAddress?.split('@')?.[0] || 'User'}
              </span>
              <span
                className="sidebar-user-tier"
                style={{ cursor: billing?.tier !== 'free' ? 'pointer' : 'default' }}
                onClick={() => billing?.tier !== 'free' && openPortal()}
                title={billing?.tier !== 'free' ? 'Manage billing' : undefined}
              >
                {tierLabel}
              </span>
            </div>
          </div>
          <button
            className="sidebar-theme-toggle"
            onClick={toggleTheme}
            title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          >
            {theme === 'dark' ? '☀️' : '🌙'}
          </button>
        </div>
      </aside>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* MAIN CONTENT AREA                                         */}
      {/* ══════════════════════════════════════════════════════════ */}
      <div className="sidebar-content">

        {/* ── Sticky top header bar ── */}
        <header className="top-header">
          <div className="top-header-left">
            <h2 className="top-header-title">{pageTitles[mainView] || 'Workspace'}</h2>
            <div className="top-header-divider" />
            <div className="top-header-workspace">
              <span className="workspace-dot" />
              <span className="workspace-label">Workspace</span>
            </div>
          </div>
          <div className="top-header-right">
            {mainView === 'scenarios' && activeAgentConfig && (
              <span style={{
                fontSize: 12, color: '#6b7280', marginRight: 8,
                display: 'flex', alignItems: 'center', gap: 5,
              }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', display: 'inline-block' }} />
                {activeAgentConfig.name}
              </span>
            )}
            {mainView === 'scenarios' && (
              <button className="top-header-cta" onClick={() => navigateTo('scenarios')}>
                + New Scenario
              </button>
            )}
            {mainView === 'zendesk' && (
              <button className="top-header-cta" onClick={() => {}}>
                + Import Tickets
              </button>
            )}
            {mainView === 'agent-config' && (
              <button className="top-header-cta" onClick={() => setTriggerNewConfig(n => n + 1)}>
                + New Config
              </button>
            )}
          </div>
        </header>

        {/* ── OAuth / checkout success banner ── */}
        {oauthMessage && (
          <div className="oauth-success-banner">
            {oauthMessage}
            <button onClick={() => setOauthMessage(null)} className="dismiss-btn">×</button>
          </div>
        )}

        {/* ── Usage warning banner (80% of limit) ── */}
        {billing && billing.usage &&
          (billing.usage.simulations_pct >= 80 || billing.usage.tickets_pct >= 80) &&
          billing.tier === 'free' && !isDemo && (
          <div style={{
            background: 'rgba(249,115,22,0.1)', borderBottom: '1px solid rgba(249,115,22,0.25)',
            padding: '10px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            fontSize: 13, color: '#94a3b8',
          }}>
            <span>
              ⚠️ You're approaching your Free plan limit —{' '}
              {billing.usage.simulations_pct >= 80
                ? `${billing.usage.simulations_used}/${billing.usage.simulations_limit} simulations used`
                : `${billing.usage.tickets_imported}/${billing.usage.tickets_limit} tickets used`}
            </span>
            <button
              onClick={() => setUpgradeModalOpen(true)}
              style={{
                background: '#7c3aed', color: '#fff', border: 'none',
                borderRadius: 6, padding: '5px 14px', fontSize: 12,
                fontWeight: 600, cursor: 'pointer',
              }}
            >
              Upgrade
            </button>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════ */}
        {/* VIEW: Home                                                */}
        {/* ══════════════════════════════════════════════════════════ */}
        <main className="main" style={{ display: mainView === 'home' ? 'block' : 'none' }}>
          <HomeTab
            onNavigate={navigateTo}
            activeAgentConfig={activeAgentConfig}
            hasKbArticles={hasKbArticles || !!(sharedKbArticles && sharedKbArticles.length > 0)}
            billing={billing}
            isDemo={isDemo}
            betaCta={betaCta}
            onDemoReset={() => setActiveAgentConfig(null)}
          />
        </main>

        {/* ══════════════════════════════════════════════════════════ */}
        {/* VIEW: Scenarios                                           */}
        {/* ══════════════════════════════════════════════════════════ */}
        <main className="main" style={{ display: mainView === 'scenarios' ? 'block' : 'none' }}>
          <ScenariosTab
            activeAgentConfig={activeAgentConfig}
            onUpgradeNeeded={() => setUpgradeModalOpen(true)}
          />
        </main>

        {/* ══════════════════════════════════════════════════════════ */}
        {/* VIEW: Zendesk Tickets                                     */}
        {/* ══════════════════════════════════════════════════════════ */}
        <main className="main" style={{ display: mainView === 'zendesk' ? 'block' : 'none' }}>
          <ZendeskTickets
            theme={theme}
            sharedKbArticles={sharedKbArticles}
            sharedAgentPrompt={sharedAgentPrompt}
            onUpgradeNeeded={() => setUpgradeModalOpen(true)}
          />
        </main>

        {/* ══════════════════════════════════════════════════════════ */}
        {/* VIEW: Benchmark                                           */}
        {/* ══════════════════════════════════════════════════════════ */}
        <main className="main" style={{ display: mainView === 'benchmark' ? 'block' : 'none' }}>
          <BenchmarkTab
            theme={theme}
            sharedKbArticles={sharedKbArticles}
            sharedAgentPrompt={sharedAgentPrompt}
            onUpgradeNeeded={() => setUpgradeModalOpen(true)}
            isDemo={isDemo}
            betaCta={betaCta}
          />
        </main>

        {/* ══════════════════════════════════════════════════════════ */}
        {/* VIEW: Agent Config                                        */}
        {/* KB configuration now lives here as a sub-section.        */}
        {/* ══════════════════════════════════════════════════════════ */}
        <main className="main" style={{ display: mainView === 'agent-config' ? 'block' : 'none' }}>
          <AgentConfigTab
            theme={theme}
            triggerNewConfig={triggerNewConfig}
            onActiveConfigChange={setActiveAgentConfig}
            onKbChange={handleKbChange}
            sharedKbArticles={sharedKbArticles}
          />
        </main>

      </div>{/* end sidebar-content */}

      {/* ── Upgrade modal ── */}
      {upgradeModalOpen && (
        <UpgradeModal
          onClose={() => setUpgradeModalOpen(false)}
          onCheckout={(tier) => {
            setUpgradeModalOpen(false);
            if (isDemo) {
              window.location.href = betaCta;
            } else {
              checkout(tier);
            }
          }}
          currentTier={billing?.tier || 'free'}
          isDemo={isDemo}
          betaCta={betaCta}
        />
      )}

    </div>
  );
}

export default App;
